import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SocketService } from '@frontend/common/util';
import { merge, Observable, of, race, throwError } from 'rxjs';
import { catchError, filter, first, ignoreElements, mergeMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Location } from '../location/location.model';

@Injectable({
  providedIn: 'root',
})
export class LocationApiService {
  constructor(private http: HttpClient, private socketSrv: SocketService) {}

  public bulk(body: { items: Location[]; correlation_id: string }, isCA?: boolean) {
    return this.http.post(`${isCA ? 'http://ca/' : ''}locations/bulk`, body);
  }

  public create(entity: Location, isCA?: boolean) {
    const correlation_id = entity.id;
    return merge(
      this.http.post(`${isCA ? 'http://ca/' : ''}locations`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError({ id: entity.id, message }))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('locations.create')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('locations.create').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id: entity.id, message }))
        )
      ).pipe(first())
    );
  }

  public delete(id, isCA?: boolean) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.request('delete', `${isCA ? 'http://ca/' : ''}locations/${id}`, { body: { correlation_id } }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(mergeMap((message) => throwError({ id, message })))
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('locations.remove')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('locations.remove').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id, message }))
        )
      ).pipe(first())
    );
  }

  public get(params?: { [key: string]: string | string[] }, isCA?: boolean): Observable<Location[]> {
    return this.http.get<Location[]>(`${isCA ? 'http://ca/' : ''}locations`, { params });
  }

  public getOne(id, isCA?: boolean) {
    return this.http.get<Location>(`${isCA ? 'http://ca/' : ''}locations/${id}`);
  }

  public update(entity, isCA?: boolean) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.put(`${isCA ? 'http://ca/' : ''}locations/${entity.id}`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError({ id: entity.id, message }))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('locations.update')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('locations.update').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id: entity.id, message }))
        )
      ).pipe(first())
    );
  }

  public updateLocationStatus(entity, isCA?: boolean) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http
        .put(`${isCA ? 'http://ca/' : ''}locations/${entity.id}/update-status`, {
          location_status_id: entity.location_status_id,
          correlation_id,
        })
        .pipe(
          ignoreElements(),
          catchError((err) =>
            of(err?.error?.message).pipe(
              mergeMap((message) => throwError({ id: entity.id, message }))
            )
          )
        ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('locations.update-status')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('locations.update-status').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id: entity.id, message }))
        )
      ).pipe(first())
    );
  }
}
